import { ref, computed } from "vue";
import { defineStore } from "pinia";
import { useRouter } from "vue-router";
// Supondo que seus tipos gerados estejam acessíveis
import type { LoginRequest, RegisterRequest } from "@/services/auth.service";
import type { UserProfile } from "@/services/user.service";
// Supondo que você tenha um serviço para chamar a API
import { authService } from "@/services/auth.service";

export const useAuthStore = defineStore("auth", () => {
  const router = useRouter();

  // --- STATE ---
  // Inicializa o estado a partir do localStorage para persistir a sessão
  const user = ref<UserProfile | null>(
    JSON.parse(localStorage.getItem("user") || "null"),
  );
  const accessToken = ref<string | null>(localStorage.getItem("accessToken"));
  const isLoading = ref(false);
  const error = ref<string | null>(null);

  // --- GETTERS ---
  const isAuthenticated = computed(() => !!accessToken.value && !!user.value);

  // --- ACTIONS ---

  /**
   * Realiza o login do usuário.
   * @param credentials - O email e senha do usuário.
   *
   */
  async function login(credentials: LoginRequest) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authService.login(credentials);
      if (response.user && response.accessToken) {
        user.value = response.user;
        accessToken.value = response.accessToken;
        localStorage.setItem("user", JSON.stringify(response.user));
        localStorage.setItem("accessToken", response.accessToken);
        await router.push("/dashboard");
      } else {
        throw new Error("Resposta de autenticação inválida.");
      }
    } catch (e: any) {
      console.error(e);
      error.value =
        e.message || "Falha ao fazer login. Verifique suas credenciais.";
      logout(false);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Realiza o registro de um novo usuário.
   * @param data - Os dados de registro do usuário.
   */
  async function register(data: RegisterRequest) {
    isLoading.value = true;
    error.value = null;
    try {
      const response = await authService.register(data);
      localStorage.setItem("accessToken", response.accessToken);
      await router.push("/dashboard");
    } catch (e: any) {
      error.value =
        e.message || "Falha ao registrar. Verifique os dados informados.";
      console.log(e);
    } finally {
      isLoading.value = false;
    }
  }

  /**
   * Realiza o logout do usuário.
   */
  function logout(redirect = true) {
    user.value = null;
    accessToken.value = null;
    localStorage.removeItem("user");
    localStorage.removeItem("accessToken");
    if (redirect) {
      router.push("/login");
    }
  }

  return {
    user,
    accessToken,
    isLoading,
    error,
    isAuthenticated,
    login,
    register,
    logout,
  };
});
